import React from 'react';
import { cn } from '../../../../components/ui/utils';

/**
 * FlightTypeToggle Component
 * Segmented control for flight type selection (One-Way/Round Trip/Multi-Leg)
 *
 * @param {Object} props
 * @param {string} props.activeType - Currently active flight type ('one-way', 'round-trip', 'multi-leg')
 * @param {Function} props.onChange - Callback when flight type changes
 * @param {boolean} props.disabled - Whether the toggle is disabled
 */
const FlightTypeToggle = ({
  activeType = 'one-way',
  onChange,
  disabled = false
}) => {
  const flightTypes = [
    { value: 'one-way', label: 'One-Way', disabled: false },
    { value: 'round-trip', label: 'Round Trip', disabled: false },
    { value: 'multi-leg', label: 'Multi-Leg', disabled: false }
  ];

  const handleTypeChange = (type) => {
    if (disabled) return;
    // Don't call onChange if clicking already active tab
    if (type === activeType) return;
    onChange?.(type);
  };

  // Handle keyboard events
  const handleKeyDown = (e, type) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTypeChange(type);
    }
  };

  return (
    <div
      role="group"
      aria-label="Flight type selection"
      className="flex gap-0 bg-gray-100 rounded-lg p-1"
    >
      {flightTypes.map(({ value, label, disabled: itemDisabled }) => {
        const isActive = activeType === value;
        const isDisabled = disabled || itemDisabled;

        return (
          <button
            key={value}
            type="button"
            onClick={() => !isDisabled && handleTypeChange(value)}
            onKeyDown={(e) => !isDisabled && handleKeyDown(e, value)}
            disabled={isDisabled}
            aria-pressed={isActive}
            className={cn(
              'flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all',
              'focus:outline-none',
              isActive
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900',
              isDisabled && 'opacity-40 cursor-not-allowed'
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default FlightTypeToggle;
